import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  AlertTriangle, AlertCircle, Info, Flame, ChevronRight, Loader2,
  CheckCircle2, ClipboardCheck, Mail,
} from 'lucide-react';
import { toast } from 'sonner';

import { apiDetail } from '../lib/apiError';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEV = {
  critical: { icon: Flame, row: 'border-red-200 dark:border-red-900/50 bg-red-50/60 dark:bg-red-950/20', text: 'text-red-600' },
  high: { icon: AlertTriangle, row: 'border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-950/20', text: 'text-amber-600' },
  medium: { icon: AlertCircle, row: 'border-blue-200 dark:border-blue-900/50 bg-blue-50/50 dark:bg-blue-950/20', text: 'text-blue-600' },
  low: { icon: Info, row: 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800', text: 'text-slate-500' },
};

const fmt = (v) => v == null ? null : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const timeAgo = (iso) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};

// PROD-003 — Unified Action Queue panel. One component, three
// consumers (Dashboard, Command Center, Construction).
// APR-10 — it approves nothing inline: a draw waiting for approval is on
// My Approvals, and is approved on its record.
export const ActionQueuePanel = ({
  limit,
  filter,               // optional (item) => bool
  title = 'Action Queue',
  emptyText = 'Nothing needs your attention',
  onCount,
}) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async (fresh = false) => {
    try {
      const r = await axios.get(`${API_URL}/api/action-queue${fresh ? '?fresh=1' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let list = r.data.items || [];
      if (filter) list = list.filter(filter);
      setItems(list);
      setError(false);
      if (onCount) onCount(list.length);
    } catch { setError(true); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // 1-click bulk retry for failed nightly rent billing.
  const retryBilling = async (item) => {
    setBusyKey(item.key);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/charge-sweep/failures/retry-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { recovered = 0, still_failing = [] } = r.data || {};
      if (still_failing.length === 0) toast.success(`Billing repaired — ${recovered} lease${recovered !== 1 ? 's' : ''} regenerated`);
      else toast.warning(`${recovered} repaired, ${still_failing.length} still failing — support has the details`);
      load(true);
    } catch (e) {
      toast.error(apiDetail(e) || 'Retry failed');
    } finally { setBusyKey(null); }
  };

  // PROD-014 — 1-click rent reminder for delinquent-tenant items.
  const sendReminder = async (item) => {
    setBusyKey(item.key);
    try {
      const r = await axios.post(
        `${API_URL}/api/payments/send-rent-reminders`,
        { tenant_id: item.tenant_id, delinquent_only: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.data.sent > 0) toast.success('Reminder email sent');
      else if (r.data.logged > 0) toast.success('Reminder logged (email not sent)');
      else toast.info('Nothing outstanding to remind');
      load(true);
    } catch (e) {
      toast.error(apiDetail(e) || 'Failed to send reminder');
    } finally { setBusyKey(null); }
  };

  const shown = limit ? items.slice(0, limit) : items;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 py-3" data-testid="action-queue-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading action queue…
      </div>
    );
  }

  return (
    <div data-testid="action-queue-panel">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1.5">
          <ClipboardCheck className="w-4 h-4 text-slate-400" />
          {title}
          {items.length > 0 && (
            <span className="text-[11px] font-bold bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-full px-1.5 py-0.5" data-testid="action-queue-count">
              {items.length}
            </span>
          )}
        </h3>
        {limit && items.length > limit && (
          <button
            onClick={() => navigate('/workflow?tab=alerts')}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-0.5"
            data-testid="action-queue-view-all"
          >
            View all {items.length} <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {error ? (
        <div className="flex items-center justify-between text-sm text-slate-500 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-3" data-testid="action-queue-error">
          <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-amber-500" /> Couldn’t load the action queue</span>
          <button onClick={() => { setLoading(true); load(); }} className="text-xs text-blue-600 dark:text-blue-400 hover:underline" data-testid="action-queue-retry">Retry</button>
        </div>
      ) : shown.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-4 py-3" data-testid="action-queue-empty">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {emptyText}
        </div>
      ) : (
        <div className="space-y-1.5">
          {shown.map(item => {
            const sev = SEV[item.severity] || SEV.low;
            const Icon = sev.icon;
            return (
              <div
                key={item.key}
                className={`flex items-center gap-3 px-3.5 py-2.5 rounded-lg border text-sm ${sev.row}`}
                data-testid={`aq-item-${item.key.replace(':', '-')}`}
              >
                <Icon className={`w-4 h-4 shrink-0 ${sev.text}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 dark:text-white truncate">{item.title}</p>
                  {item.detail && <p className="text-[11px] text-slate-500 truncate">{item.detail}</p>}
                  {item.last_reminded_at && (
                    <p className="text-[10px] text-emerald-600 dark:text-emerald-400" data-testid={`aq-last-reminded-${item.tenant_id}`}>
                      Reminded {timeAgo(item.last_reminded_at)}
                    </p>
                  )}
                </div>
                {item.amount != null && item.amount > 0 && (
                  <span className="text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200 shrink-0">{fmt(item.amount)}</span>
                )}
                {item.kind === 'billing_failure' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50 dark:text-red-300 dark:border-red-700 shrink-0"
                    disabled={busyKey === item.key}
                    onClick={() => retryBilling(item)}
                    data-testid="aq-retry-billing"
                    title="Retry billing for every lease listed in Billing Exceptions"
                  >
                    {busyKey === item.key ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Retry All'}
                  </Button>
                )}
                {item.kind === 'rent_delinquent' && item.tenant_id && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-amber-700 border-amber-300 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-700 shrink-0"
                    disabled={busyKey === item.key}
                    onClick={() => sendReminder(item)}
                    data-testid={`aq-remind-${item.tenant_id}`}
                    title="Send a rent reminder email to this tenant"
                  >
                    {busyKey === item.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Mail className="w-3 h-3 mr-1" />Remind</>}
                  </Button>
                )}
                {item.link && (
                  <button
                    onClick={() => navigate(item.link)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center shrink-0"
                    data-testid={`aq-open-${item.key.replace(':', '-')}`}
                  >
                    Open <ChevronRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActionQueuePanel;
